import { clsx } from 'clsx'
import { X } from 'lucide-react'
import { useEffect, useRef } from 'react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
}: ModalProps): React.JSX.Element | null {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [open])

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>): void {
    const dialog = dialogRef.current
    if (!dialog) return
    const rect = dialog.getBoundingClientRect()
    const clickedOutside =
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    if (clickedOutside) {
      onClose()
    }
  }

  if (!open) return null

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={handleBackdropClick}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
      className={clsx(
        'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full rounded-xl bg-surface-0 p-0 shadow-xl backdrop:bg-black/50 backdrop:backdrop-blur-sm',
        sizeClasses[size],
      )}
    >
      <div className="flex items-center justify-between border-b border-surface-3 px-6 py-4">
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-surface-2">
          <X className="h-5 w-5 text-text-tertiary" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </dialog>
  )
}
